import { useForm, SubmitHandler } from 'react-hook-form';
import { Input, Button, FormControl, FormLabel, Link, Box, useToast, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from 'react-query';
import { signUp } from '../../apis/auth';
import { useUser } from '../../hooks/useUser';
import { AxiosError } from 'axios';
import { useDomainConfig } from '../../hooks/useDomainConfig';
interface SignUpFormInputs {
	name: string;
	email: string;
	password: string;
}
export interface ErrorResponse {
	message: string;
}
interface SignUpFormProps {
	onSignInClick: () => void;
}
const SignUpForm = ({ onSignInClick }: SignUpFormProps) => {
	const passwordPattern = /^(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<SignUpFormInputs>();
	const { refetch } = useUser();
	const { data: domainConfig } = useDomainConfig({ uiHost: window.location.host });
	const { mutate: signUpMutate, isLoading: isSignUpLoading } = useMutation(signUp, {
		onSuccess: () => {
			refetch();
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'User signup failed',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});

	const onSubmit: SubmitHandler<SignUpFormInputs> = data => {
		signUpMutate({
			...data,
			domainKey: domainConfig?.key,
		});
	};
	const toast = useToast();

	const termsOfServiceLink = 'https://www.prepleaf.com/terms_&_conditions';
	const privacyPolicyLink = 'https://www.prepleaf.com/privacy_policy';

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormControl id="name" mb="24px" isInvalid={errors.name ? true : false}>
				<Text as={FormLabel} textStyle="body2-md">
					Full Name
				</Text>
				<Input placeholder="Enter your full name" type="text" {...register('name', { required: 'Name is required' })} />
				{errors.name && <span>{errors.name.message}</span>}
			</FormControl>
			<FormControl id="email" mb="24px" isInvalid={errors.email ? true : false}>
				<Text as={FormLabel} textStyle="body2-md">
					Email
				</Text>
				<Input placeholder="julie@example.com" type="email" {...register('email', { required: 'Email is required' })} />
				{errors.email && <span>{errors.email.message}</span>}
			</FormControl>
			<FormControl id="password" mb="24px" isInvalid={errors.password ? true : false}>
				<Text as={FormLabel} textStyle="body2-md">
					Password
				</Text>
				<Input
					placeholder="Enter your password"
					type="password"
					{...register('password', {
						required: 'Password is required',
						minLength: {
							value: 8,
							message: 'Password must be at least 8 characters long',
						},
					})}
				/>
				{errors.password && (
					<Text mt="6px" color="#D61E27">
						{errors.password.message}
					</Text>
				)}
			</FormControl>

			<Box border="0px solid red" textStyle="body2" textAlign="center" mb="24px" color="#544D4F">
				By signing up, I accept the Prepleaf{' '}
				<Link as={RouterLink} color="#386AFA" to={termsOfServiceLink} textDecor="underline" target="_blank">
					Terms of Service
				</Link>{' '}
				and acknowledge the{' '}
				<Link as={RouterLink} color="#386AFA" to={privacyPolicyLink} textDecor="underline" target="_blank">
					Privacy Policy.
				</Link>
			</Box>

			<Box display={'flex'} gap="8px" alignItems={'center'}>
				<Button type="submit" w="full" size="lg" variant="primary" isLoading={isSignUpLoading}>
					Sign Up
				</Button>
			</Box>

			<Box display="flex" gap="8px" color="#544D4F" textStyle="body1-md" justifyContent="center" textAlign="center" mt="40px" flexWrap="wrap">
				Already have an account?
				<Link
					onClick={
						onSignInClick
							? e => {
									e.preventDefault();
									onSignInClick();
							  }
							: undefined
					}
					as={RouterLink}
					color="#386AFA"
					to="/sign-in"
				>
					Sign In
				</Link>
			</Box>
		</form>
	);
};
export default SignUpForm;
